import React, { createContext, useContext, useReducer } from 'react'
import PropTypes from 'prop-types'

const UIContext = createContext()

const initialState = {
  // Modal states
  modals: {
    cartDrawer: false,
    productQuickView: false,
    searchModal: false,
    filterModal: false,
  },
  
  // Loading states
  loading: {
    page: false,
    products: false,
    cart: false,
    checkout: false,
  },
  
  // Alert states
  alerts: [],
  
  // Sidebar states
  sidebar: {
    mobileMenu: false,
    filterSidebar: false,
    userMenu: false,
  },
  
  // Search state
  search: {
    isOpen: false,
    query: '',
    suggestions: [],
  },
  
  // Theme state
  theme: 'light',
}

const uiReducer = (state, action) => {
  switch (action.type) {
    case 'TOGGLE_MODAL':
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload]: !state.modals[action.payload],
        },
      }
    
    case 'CLOSE_MODAL':
      return {
        ...state,
        modals: {
          ...state.modals,
          [action.payload]: false,
        },
      }
    
    case 'SET_LOADING':
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.key]: action.payload.value,
        },
      }
    
    case 'ADD_ALERT':
      return {
        ...state,
        alerts: [...state.alerts, action.payload],
      }
    
    case 'REMOVE_ALERT':
      return {
        ...state,
        alerts: state.alerts.filter(alert => alert.id !== action.payload),
      }
    
    case 'TOGGLE_SIDEBAR':
      return {
        ...state,
        sidebar: {
          ...state.sidebar,
          [action.payload]: !state.sidebar[action.payload],
        },
      }
    
    case 'CLOSE_SIDEBAR':
      return {
        ...state,
        sidebar: {
          ...state.sidebar,
          [action.payload]: false,
        },
      }
    
    case 'SET_SEARCH_STATE':
      return {
        ...state,
        search: {
          ...state.search,
          ...action.payload,
        },
      }
    
    case 'TOGGLE_THEME':
      return {
        ...state,
        theme: state.theme === 'light' ? 'dark' : 'light',
      }
    
    case 'RESET_UI':
      return {
        ...state,
        modals: initialState.modals,
        sidebar: initialState.sidebar,
        search: initialState.search,
      }
    
    default:
      return state
  }
}

export const UIProvider = ({ children }) => {
  const [state, dispatch] = useReducer(uiReducer, initialState)

  const toggleModal = (modalName) => {
    dispatch({ type: 'TOGGLE_MODAL', payload: modalName })
  }

  const closeModal = (modalName) => {
    dispatch({ type: 'CLOSE_MODAL', payload: modalName })
  }

  const setLoading = (key, value) => {
    dispatch({ type: 'SET_LOADING', payload: { key, value } })
  }

  const addAlert = (alert) => {
    const id = Date.now().toString()
    const newAlert = { id, ...alert }
    dispatch({ type: 'ADD_ALERT', payload: newAlert })
    
    // Auto-remove alert after duration
    if (alert.duration !== 0) {
      setTimeout(() => {
        removeAlert(id)
      }, alert.duration || 5000)
    }
    
    return id
  }

  const removeAlert = (alertId) => {
    dispatch({ type: 'REMOVE_ALERT', payload: alertId })
  }

  const toggleSidebar = (sidebarName) => {
    dispatch({ type: 'TOGGLE_SIDEBAR', payload: sidebarName })
  }

  const closeSidebar = (sidebarName) => {
    dispatch({ type: 'CLOSE_SIDEBAR', payload: sidebarName })
  }

  const setSearchState = (searchData) => {
    dispatch({ type: 'SET_SEARCH_STATE', payload: searchData })
  }

  const toggleTheme = () => {
    dispatch({ type: 'TOGGLE_THEME' })
  }

  const resetUI = () => {
    dispatch({ type: 'RESET_UI' })
  }

  const value = {
    ...state,
    toggleModal,
    closeModal,
    setLoading,
    addAlert,
    removeAlert,
    toggleSidebar,
    closeSidebar,
    setSearchState,
    toggleTheme,
    resetUI,
  }

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>
}

UIProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useUI = () => {
  try {
    const context = useContext(UIContext)
    if (!context) {
      console.warn('useUI must be used within a UIProvider')
      // Return a default context to prevent crashes
      return {
        theme: 'light',
        sidebarOpen: false,
        modalOpen: false,
        loading: false,
        notifications: [],
        setTheme: () => {},
        toggleSidebar: () => {},
        openModal: () => {},
        closeModal: () => {},
        setLoading: () => {},
        addNotification: () => {},
        removeNotification: () => {},
        clearNotifications: () => {}
      }
    }
    return context
  } catch (error) {
    console.error('UIContext error:', error)
    return {
      theme: 'light',
      sidebarOpen: false,
      modalOpen: false,
      loading: false,
      notifications: [],
      setTheme: () => {},
      toggleSidebar: () => {},
      openModal: () => {},
      closeModal: () => {},
      setLoading: () => {},
      addNotification: () => {},
      removeNotification: () => {},
      clearNotifications: () => {}
    }
  }
}
